import { useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  setModalData,
  setState,
  setActiveTrade,
  setTradeData,
  resetTradeData,
  addToastNotification,
} from "@/store/globalSlice"
import { socket } from "@/utils/utils"
import {
  getCurrentPlayer,
  getPlayerByPlayerOrder,
  groupPropertiesByGroupId,
} from "@/utils/gameHelpers"
import { mapDesigns } from "@mono/mapDesigns"
import { allPieces } from "@/utils/data"
import Money from "@/Components/Money"
import ModalCloseButton from "@/Components/ModalCloseButton"
import CompactMoneySlider from "@/Pages/Trade/CompactMoneySlider"
import MobileCashSheet from "@/Pages/Trade/MobileCashSheet"
import PropertiesSet from "@/Pages/Game/PropertiesSet"
import TradeScreenPropertyCard from "@/Pages/Trade/TradeScreenPropertyCard"
import TradeActionButton from "@/Pages/Trade/TradeActionButton"
import { useFitTradeCards } from "@/Hooks/useFitTradeCards"
import { tradeOriginBadge, getTradeOneSidedError } from "@/utils/tradeHelpers"
import { playSmallErrorSound } from "@/utils/audioManager"
import { ThreeDots } from "react-loader-spinner"

const CreateTradeModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const tradeData = useAppSelector((state) => state.global.tradeData)
  const tempTradeData = useAppSelector((state) => state.global.tempTradeData)
  const activeTrade = tradeData.activeTrade
  const map = mapDesigns.find((m) => m.id === myGame?.settings.mapId)

  // viewedTradeId distinguishes "viewing an already-offered trade" (from the
  // sidebar list) from "creating a new offer".
  const viewedTradeId = useAppSelector((state) => state.global.viewedTradeId)
  const isViewMode = !!viewedTradeId
  // Re-editing a trade that is already on the table: the drawer goes back to its
  // compose form (board and cards clickable again) while the original offer stays
  // live until the replacement is actually sent. `activeTrade.id` still points at
  // that original, which is what gets withdrawn on Offer.
  const isUpdateMode = tradeData.status === "updating-offer"
  // The offered player countering instead of accepting/declining. The draft has
  // already been flipped into their perspective by then (see onClickOnNegotiate),
  // so from here on they *are* the offering player and every compose control
  // works unchanged; `replacesTradeId` is what still ties the draft back to the
  // offer it came from.
  const isNegotiateMode = tradeData.status === "negotiating"
  const viewedTrade = viewedTradeId
    ? myGame?.situation?.activeTrades?.find((t) => t.id === viewedTradeId)
    : undefined

  const thisPlayer = getCurrentPlayer(myGame as IGameDetails)
  const isOfferingPlayer =
    thisPlayer?.order === activeTrade?.offeringPlayerOrder
  const isOfferedPlayer = thisPlayer?.order === activeTrade?.offeredPlayerOrder

  // Both players and their tradable property lists are resolved up here, above
  // the early returns, because the card-fitting hook below needs to know how
  // much content it will have to fit and hooks can't run conditionally.
  const housesData = myGame?.situation?.housesData || []
  const tradableProperties = (player?: IPlayer): ICell[] =>
    (player?.properties || [])
      // Properties with houses on them can't be traded, so they never show up.
      .filter(
        (order) =>
          !(housesData.find((h) => h.propertyOrder === order)?.houses || 0)
      )
      .map((order) => map?.cells.find((cell) => cell.order === order) as ICell)
      .filter(Boolean)

  const offeringPlayer = getPlayerByPlayerOrder(
    myGame,
    activeTrade?.offeringPlayerOrder as number
  )
  const offeredPlayer = getPlayerByPlayerOrder(
    myGame,
    activeTrade?.offeredPlayerOrder as number
  )
  const offeringProperties = tradableProperties(offeringPlayer)
  const offeredProperties = tradableProperties(offeredPlayer)

  // Drawer slide animation. `open` drives the transform: it flips to true one
  // frame after mount (slide in) and back to false on close (slide out). The
  // trade state itself is only cleared after the slide-out has finished, so the
  // panel keeps its contents while animating away.
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(id)
  }, [])

  // Which way the cash flows. It has to be local state because the direction
  // must survive the amount being 0 — at which point moneyGiven and moneyTaken
  // are both 0 and neither can tell us who the payer was meant to be.
  const [cashFromOffering, setCashFromOffering] = useState(
    !activeTrade?.moneyTaken
  )

  // Free-typing buffer for the cash field. Typing "250" over a "10" has to pass
  // through an empty string, which the committed amount can't represent — null
  // means "there is no edit in progress, just mirror the trade".
  const [cashDraft, setCashDraft] = useState<string | null>(null)

  // Phone layout. The drawer is the left sidebar, which is (100vw-100vh)/2 —
  // roomy on a desktop, about 200px on a landscape phone. Two things change at
  // that size: the cash card collapses to one row and hands its controls to a
  // sheet with the whole box to itself (see MobileCashSheet), and the drawer
  // gains a way to get out of the player's way without being closed.
  const isMobile = useAppSelector((state) => state.global.isMobile)
  const [cashSheetOpen, setCashSheetOpen] = useState(false)
  const [minimized, setMinimized] = useState(false)

  // Both are phone-only states over a draft that outlives them. The sheet edits
  // an amount, so it belongs to composing: a rotation onto a wide screen or an
  // offer that lands and turns the drawer into a view of itself would leave it
  // standing over a drawer that has no way back to it. Minimizing is not
  // compose-only — folding an offer away to look at the board before answering
  // it is the case that wants it most — so it survives everything but the
  // screen getting wide enough not to need it.
  useEffect(() => {
    if (isMobile && !isViewMode) return
    setCashSheetOpen(false)
  }, [isMobile, isViewMode])

  useEffect(() => {
    if (isMobile) return
    setMinimized(false)
  }, [isMobile])

  // A player on a negative balance can't put any cash into a deal: the server
  // only lets a side pay when it can cover the amount, so the most they could
  // ever hand over is M0 (see isTradeStillValid — giving 0 is exactly what keeps
  // trading during bankruptcy legal in the first place). So when one side is in
  // the red and the other isn't, which way the cash goes stops being a choice:
  // the solvent player pays and the player in debt is the one being paid. The
  // drawer settles it there and drops Swap, rather than opening on a dead field
  // and leaving the player to work out that they have to flip it — which is the
  // one thing a player raising cash out of bankruptcy has to get right. The
  // direction it lands on is already spelled out by the sentence above it, so
  // the missing button is left to speak for itself.
  //
  // Composing only: an offered trade already states its own direction, and that
  // is what the other player has to answer.
  const offeringInDebt = (offeringPlayer?.money || 0) < 0
  const offeredInDebt = (offeredPlayer?.money || 0) < 0
  const forcedCashFromOffering =
    !isViewMode && offeringInDebt !== offeredInDebt ? offeredInDebt : null

  // Point the draft itself the same way, not just the sentence. An amount left
  // sitting on the debtor's side — a draft restored from a declined trade, a
  // negotiation flip, or a balance that went negative while the drawer was open
  // — would otherwise still be sent, and the server would reject the whole trade
  // over a payment that side can never cover.
  useEffect(() => {
    if (forcedCashFromOffering === null) return
    setCashFromOffering(forcedCashFromOffering)
    const stranded =
      (forcedCashFromOffering
        ? activeTrade?.moneyTaken
        : activeTrade?.moneyGiven) || 0
    if (!stranded) return
    // Carried across rather than dropped, clamped to what the payer now holds.
    const payer = forcedCashFromOffering ? offeringPlayer : offeredPlayer
    const carried = Math.min(stranded, payer?.money || 0)
    setCashDraft(null)
    dispatch(
      setActiveTrade(
        forcedCashFromOffering
          ? { moneyGiven: carried, moneyTaken: 0 }
          : { moneyTaken: carried, moneyGiven: 0 }
      )
    )
  }, [
    forcedCashFromOffering,
    activeTrade?.moneyGiven,
    activeTrade?.moneyTaken,
  ])

  // The panel shrinks its property cards until everything fits its height cap,
  // so it never scrolls and never clips. Re-fit whenever the content changes
  // shape: different players, different property counts, or a row appearing or
  // disappearing (the cash row, the button row).
  const panelRef = useRef<HTMLDivElement>(null)
  const hasPickedAnything =
    !!(activeTrade?.givenProperties?.length || activeTrade?.takenProperties?.length)
  useFitTradeCards(
    panelRef,
    [
      activeTrade?.offeringPlayerOrder,
      activeTrade?.offeredPlayerOrder,
      offeringProperties.length,
      offeredProperties.length,
      isViewMode,
      isUpdateMode,
      isNegotiateMode,
      (activeTrade?.moneyGiven || 0) > 0 || (activeTrade?.moneyTaken || 0) > 0,
      // Flipping the payer can swap the cash slider for the "no cash" note,
      // which is a different height, and locking the direction drops the Swap
      // button off the card altogether.
      cashFromOffering,
      forcedCashFromOffering,
      // The phone's collapsed cash row is a different height from the full
      // card, and restoring from the pill mounts a brand new box with none of
      // the sizing custom properties on it — both have to re-fit.
      isMobile,
      minimized,
    ].join("|")
  )

  // Everything this drawer puts up: the two flags that mount it and the draft
  // itself. Split out from the slide-out below because the safety net further
  // down has to clear the same state with no animation — in that case there is
  // nothing on screen left to animate away.
  const closeTradeUi = () => {
    dispatch(setState({ viewedTradeId: null }))
    dispatch(setModalData({ trade: { visible: false } }))
    dispatch(resetTradeData(undefined as any))
  }

  const requestClose = () => {
    setOpen(false)
    window.setTimeout(closeTradeUi, 300)
  }

  // If the viewed trade disappears (accepted / declined / withdrawn by someone),
  // close the drawer.
  useEffect(() => {
    if (isViewMode && !viewedTrade) {
      requestClose()
    }
  }, [isViewMode, viewedTrade])

  // Safety net for the early return below. Being mounted is what dims the board
  // into trade mode, and the close button in this drawer is the only thing that
  // ever clears that — so a drawer that is mounted but renders nothing is a
  // dead end: no panel, no button, and a board left dimmed for the rest of the
  // game. It is worst for a player on a negative balance, who is locked out
  // entirely: the raise-cash modal stays suppressed for as long as the trade
  // flag is up, and on their own turn Roll and End Turn are both withheld.
  // So whatever leaves the flags saying "open" over a draft that can't be
  // drawn, clear them here instead of rendering nothing.
  //
  // Not while the player chooser is up: there the draft is deliberately without
  // an offered player, and picking one completes it.
  const isChoosingPlayer = tempTradeData?.status === "choosing-player"
  const canRender = !!activeTrade && !!offeringPlayer && !!offeredPlayer
  useEffect(() => {
    if (canRender || isChoosingPlayer) return
    closeTradeUi()
  }, [canRender, isChoosingPlayer])

  // Every send path runs this first: an offer that isn't a two-way deal is
  // refused with a toast and the drawer stays open on the draft, so the player
  // can fix it rather than having to rebuild it. Kept as a guard on the action
  // instead of a disabled Offer button — a greyed-out button says "no" without
  // ever saying why, and "why" is the whole point here.
  const blockedByOneSidedTrade = () => {
    const error = getTradeOneSidedError(activeTrade as ITrade)
    if (!error) return false
    // Same sound as a declined trade — the offer didn't go anywhere either way,
    // and the toast is up in the far corner from the drawer the player is
    // looking at, so the cue is what points them at it.
    playSmallErrorSound()
    dispatch(
      addToastNotification({
        type: "general",
        messageType: "error",
        message: error,
      })
    )
    return true
  }

  const onClickOnOffer = () => {
    if (!activeTrade) return
    if (blockedByOneSidedTrade()) return
    socket.emit("trade.trade-offered", myGame?.id || "", {
      ...activeTrade,
      // A first offer carries no origin, and cleared explicitly because this
      // draft may have been restored from a previously declined trade that did
      // (see lastDeclinedTrades).
      origin: undefined,
    })
    requestClose()
  }
  const onClickOnAccept = () => {
    if (!activeTrade) return
    socket.emit("trade.trade-accepted", myGame!.id, activeTrade as ITrade)
    requestClose()
  }
  const onClickOnDecline = () => {
    if (!activeTrade) return
    socket.emit("trade.trade-declined", myGame!.id, activeTrade as ITrade)
    requestClose()
  }
  const onClickOnWithdraw = () => {
    if (!activeTrade) return
    socket.emit(
      "trade.trade-withdrawn",
      myGame!.id,
      activeTrade as ITrade
    )
    requestClose()
  }

  // Go from "waiting for a response" back to composing, with the offered trade
  // as the starting draft. The offer on the table is deliberately left alone —
  // it is only replaced if the player actually sends the updated one.
  const onClickOnUpdate = () => {
    dispatch(setTradeData({ status: "updating-offer" }))
    // Leaving view mode is what re-enables editing; the drawer stays mounted
    // because the trade modal flag goes up in the same batch.
    dispatch(setState({ viewedTradeId: null }))
    dispatch(setModalData({ trade: { visible: true } }))
  }

  // Send the edited offer. The old trade is withdrawn and the new one goes out
  // without an id, so the server mints a fresh one and restarts the countdown —
  // re-offering under the old id would be a no-op, since the offer handler keeps
  // the trade it already has for that id.
  const onClickOnUpdatedOffer = () => {
    if (!activeTrade) return
    // Checked before the withdraw, so a rejected edit leaves the original offer
    // standing on the table exactly as it was.
    if (blockedByOneSidedTrade()) return
    if (activeTrade.id) {
      socket.emit(
        "trade.trade-withdrawn",
        myGame!.id,
        activeTrade as ITrade
      )
    }
    socket.emit("trade.trade-offered", myGame?.id || "", {
      ...activeTrade,
      id: undefined,
      offeredAt: undefined,
      // Stamped at send time rather than when Update was pressed, so a draft
      // that gets discarded never leaves the marker behind.
      origin: "updated",
    })
    requestClose()
  }

  // Counter-offer. The offered player takes the trade they were sent and starts
  // editing it, which means the two sides swap roles: they become the offering
  // player, so what was being given to them is now what they are asking for and
  // vice versa. Flipping the draft here (rather than teaching every control who
  // is composing) is what lets the whole compose UI — board clicks, cards, the
  // cash row, Reset — work in negotiation without a single change.
  const onClickOnNegotiate = () => {
    if (!activeTrade) return
    dispatch(
      setTradeData({
        status: "negotiating",
        activeTrade: {
          ...activeTrade,
          offeringPlayerOrder: activeTrade.offeredPlayerOrder,
          offeredPlayerOrder: activeTrade.offeringPlayerOrder,
          givenProperties: [...(activeTrade.takenProperties || [])],
          takenProperties: [...(activeTrade.givenProperties || [])],
          moneyGiven: activeTrade.moneyTaken || 0,
          moneyTaken: activeTrade.moneyGiven || 0,
          // The offer being countered. The server drops it when this draft is
          // sent; until then it stays live, so Discard costs nothing.
          replacesTradeId: activeTrade.id,
          id: undefined,
          offeredAt: undefined,
        },
      })
    )
    // Same as the update flow: leaving view mode re-enables editing, and the
    // modal flag goes up in the same batch so the drawer never unmounts.
    dispatch(setState({ viewedTradeId: null }))
    dispatch(setModalData({ trade: { visible: true } }))
  }

  // Send the counter-offer. It goes out with no id so the server mints a fresh
  // one and restarts the countdown, and with replacesTradeId set so the offer it
  // answers is removed server-side in the same update.
  const onClickOnNegotiatedOffer = () => {
    if (!activeTrade) return
    if (blockedByOneSidedTrade()) return
    socket.emit("trade.trade-offered", myGame?.id || "", {
      ...activeTrade,
      id: undefined,
      offeredAt: undefined,
      // Same as the update flow: only a sent counter is marked as one.
      origin: "negotiated",
    })
    requestClose()
  }

  // Abandon the edits only. The trade being updated or negotiated is still on
  // the table and stays there — discarding must never withdraw it.
  const onClickOnDiscard = () => {
    requestClose()
  }

  if (!activeTrade || !offeringPlayer || !offeredPlayer) return null

  // Buttons depend on the viewer's role.
  const hasOfferedPlayer =
    activeTrade.offeredPlayerOrder !== undefined &&
    activeTrade.offeredPlayerOrder !== null
  const showOffer =
    !isViewMode &&
    !isUpdateMode &&
    !isNegotiateMode &&
    isOfferingPlayer &&
    hasOfferedPlayer
  // Accept / Negotiate / Decline are the three answers to an incoming offer.
  const showAcceptDecline = isViewMode && isOfferedPlayer
  const showWithdraw = isViewMode && isOfferingPlayer
  // Withdraw and Update sit together: both are the offering player's ways out of
  // waiting, one dropping the offer and one reworking it.
  const showUpdate = isViewMode && isOfferingPlayer
  const showUpdateActions = isUpdateMode && isOfferingPlayer && hasOfferedPlayer
  // The draft is flipped on entry, so the negotiator reads as the offering
  // player here just like any other composer.
  const showNegotiateActions =
    isNegotiateMode && isOfferingPlayer && hasOfferedPlayer

  // Tells the offered player that what they're looking at isn't a first offer:
  // it either replaces one they were already weighing up, or it's the answer to
  // one they sent themselves. Only they get it — the player who pressed Update
  // or Negotiate already knows what they did.
  const originBadge =
    isViewMode && isOfferedPlayer && activeTrade.origin
      ? tradeOriginBadge(activeTrade.origin)
      : null

  const statusText = () => {
    if (isNegotiateMode) return "Negotiating trade"
    if (isUpdateMode) return "Updating trade"
    if (!isViewMode) return "Create your offer"
    if (isOfferedPlayer) return "Trade offered to you"
    if (isOfferingPlayer) return "Waiting for response"
    return "Trade offer"
  }

  // A trade only ever moves money in one direction, so there is exactly one
  // amount and one direction to edit — hence a single shared cash row between
  // the two panels rather than a slider inside each of them. A non-zero amount
  // tells us the direction on its own; only at zero do we fall back to what the
  // player last chose.
  const moneyGiven = activeTrade.moneyGiven || 0
  const moneyTaken = activeTrade.moneyTaken || 0
  const cashPaidByOffering =
    forcedCashFromOffering !== null
      ? forcedCashFromOffering
      : moneyGiven > 0
      ? true
      : moneyTaken > 0
      ? false
      : cashFromOffering
  const cashAmount = cashPaidByOffering ? moneyGiven : moneyTaken
  const cashPayer = cashPaidByOffering ? offeringPlayer : offeredPlayer
  const cashReceiver = cashPaidByOffering ? offeredPlayer : offeringPlayer

  // Reset is only for the player composing the offer; there is nothing to reset
  // once a trade has been sent. It stays mounted but disabled on an untouched
  // offer so the hint row it sits on never reflows mid-edit.
  const showReset = !isViewMode && isOfferingPlayer
  const hasAnySelection = hasPickedAnything || cashAmount > 0
  const resetSelections = () => {
    setCashFromOffering(true)
    dispatch(
      setActiveTrade({
        givenProperties: [],
        takenProperties: [],
        moneyGiven: 0,
        moneyTaken: 0,
      })
    )
  }

  const setCash = (value: number) =>
    dispatch(
      setActiveTrade(
        cashPaidByOffering
          ? { moneyGiven: value, moneyTaken: 0 }
          : { moneyTaken: value, moneyGiven: 0 }
      )
    )

  // Everything except the field itself commits through here: a value chosen
  // somewhere else (a chip, the slider, a flip) has to clear the typing buffer,
  // or the field would keep showing the half-typed number it was left on.
  const applyCash = (value: number) => {
    setCashDraft(null)
    setCash(value)
  }

  const onCashInput = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, "")
    if (!digits) {
      setCashDraft("")
      setCash(0)
      return
    }
    // Clamped while it is typed rather than on blur: a field reading M9999 over
    // a deal that is only carrying M500 is exactly the quiet mismatch this card
    // exists to get rid of.
    const next = Math.min(parseInt(digits, 10), cashPayer.money)
    setCashDraft(String(next))
    setCash(next)
  }

  // What the payer is left holding — the number a player actually reasons about
  // when deciding whether an amount is sensible, and the reason the slider stops
  // where it does.
  const payerLeft = Math.max(0, cashPayer.money - cashAmount)
  const hasCash = cashAmount > 0

  // Carry the amount across when the payer flips, clamped to what the new payer
  // can actually afford.
  const flipCashDirection = () => {
    if (forcedCashFromOffering !== null) return
    const carried = Math.min(cashAmount, cashReceiver.money)
    setCashFromOffering(!cashPaidByOffering)
    setCashDraft(null)
    dispatch(
      setActiveTrade(
        cashPaidByOffering
          ? { moneyGiven: 0, moneyTaken: carried }
          : { moneyTaken: 0, moneyGiven: carried }
      )
    )
  }

  const pieceOf = (player: IPlayer) =>
    allPieces.find((pc) => pc.id === player.pieceId)

  // The viewer is always addressed as "You" rather than by their own username —
  // in a panel with two similar-looking names it's the fastest way to tell at a
  // glance which side of the trade is yours.
  const isYou = (player: IPlayer) => player.order === thisPlayer?.order

  const nameOf = (player: IPlayer) =>
    isYou(player)
      ? "You"
      : myGame?.players.find((u) => u.email === player.id)?.username ||
        "Player"

  // A player's piece, inline in the cash sentence. Small enough to sit in a line
  // of text and still the fastest way to match the name to the panel it came
  // from — the two banners either side of this card show the same piece.
  const piecePip = (player: IPlayer) => {
    const piece = pieceOf(player)
    return (
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-black/40 ring-1 ring-white/10">
        {piece?.imgSrc && (
          <img className="h-3.5 w-3.5 object-contain" src={piece.imgSrc} />
        )}
      </span>
    )
  }

  // The same pip in raw px, for the phone's collapsed cash row. `piecePip` is
  // sized in rem, and index.css halves the root font size below 1000px — the
  // only screens that row exists on — so it would land there as a 10px dot with
  // a 7px piece in it. Kept a step under the player banners' own avatars (2rem,
  // so 16px on a phone) because this row summarises them rather than repeating
  // them.
  const piecePipPx = (player: IPlayer) => {
    const piece = pieceOf(player)
    return (
      <span className="flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full bg-black/40 ring-1 ring-white/10">
        {piece?.imgSrc && (
          <img
            className="h-[10px] w-[10px] object-contain"
            src={piece.imgSrc}
          />
        )}
      </span>
    )
  }

  // "to you" rather than "to You": the viewer is named "You" when they are the
  // subject of the sentence, but the same word mid-sentence should read like the
  // pronoun it is.
  const objectNameOf = (player: IPlayer) =>
    isYou(player) ? "you" : nameOf(player)

  // Subject-verb agreement. The viewer is named "You", which takes "pay" — every
  // other player is a third-person singular and takes "pays". Worth a helper
  // because the same sentence is written twice: once for the deal as it stands
  // and once for what Swap would turn it into.
  const paysVerb = (player: IPlayer) => (isYou(player) ? "pay" : "pays")

  const renderPanel = (
    player: IPlayer,
    properties: ICell[],
    // Which way this player's picked properties travel. Purely a labelling
    // concern: it is what makes the two otherwise identical panels tell apart at
    // a glance, and it is always "away from this panel" — the top panel's picks
    // go down to the player below and vice versa.
    outgoing: "down" | "up"
  ) => {
    const piece = pieceOf(player)
    const propertyGroups = groupPropertiesByGroupId(properties)
    // How much of this player's side is already in the deal. Shown as a chip on
    // the banner so each panel carries its own running total instead of the
    // player having to re-scan the cards for highlighted ones.
    const pickedCount =
      (outgoing === "down"
        ? activeTrade.givenProperties?.length
        : activeTrade.takenProperties?.length) || 0
    return (
      // Recessed rather than raised: both property panels sit *below* the
      // drawer's surface so the cash card between them is the only thing that
      // comes forward. Previously all three were the same lifted grey box, which
      // is exactly why the one editable control got lost among them.
      <div className="rounded-xl border border-white/5 bg-black/20 overflow-hidden">
        {/* Player banner — deliberately the quietest row in the drawer. It is
            reference information (who, how much they hold), not something to
            act on, so everything here is a size step below the cash card.
            Balance stays emerald; only the cash row is allowed to be amber. */}
        <div
          className="flex items-center gap-2 px-2.5 bg-white/5 border-b border-white/5"
          style={{ paddingBlock: "var(--tc-banner-py, 9px)" }}
        >
          <div className="flex items-center justify-center shrink-0 h-8 w-8 rounded-full bg-black/40">
            {piece?.imgSrc && (
              <img className="h-6 w-6 object-contain" src={piece.imgSrc} />
            )}
          </div>
          <span className="flex-1 min-w-0 font-poppins font-semibold text-white/85 text-base truncate">
            {nameOf(player)}
          </span>
          {pickedCount > 0 && (
            <span
              title={`${pickedCount} propert${
                pickedCount === 1 ? "y" : "ies"
              } from ${nameOf(player)} in this deal`}
              data-testid="trade.picked-count"
              className="shrink-0 flex items-center gap-1 rounded-full border border-amber-300/30 bg-amber-400/20 px-2 py-0.5 font-poppins font-semibold text-amber-200 text-xs leading-none"
            >
              <i
                className={classNames(
                  "text-[0.625rem]",
                  outgoing === "down"
                    ? "fa-solid fa-arrow-down"
                    : "fa-solid fa-arrow-up"
                )}
              />
              {pickedCount}
            </span>
          )}
          <Money
            money={player.money}
            className="shrink-0 text-emerald-300/90 font-poppins font-semibold gap-0.5 text-lg leading-none"
            iconClass="h-1/2"
          />
        </div>

        {/* Property grid. No scrollbar and no clipping: the cards themselves
            shrink (useFitTradeCards) until every one of them fits. Row spacing
            comes from each card's own bottom margin, which is why there is no
            bottom padding here. */}
        <div
          className="flex flex-wrap content-start px-2"
          style={{
            columnGap: "var(--tc-gapx, 0.3125rem)",
            paddingTop: "var(--tc-grid-pt, 8px)",
          }}
        >
          {properties.length === 0 && (
            <div className="w-full text-center text-white/50 text-sm font-poppins py-2">
              No tradable properties
            </div>
          )}
          {propertyGroups.map((groupObject) => {
            const grp = map?.groups.find(
              (g) => g.id === groupObject.groupId
            ) as IPropertyGroup
            return (
              <PropertiesSet
                forTradeScreen
                key={grp.id}
                group={grp}
                properties={groupObject.properties}
                PropertCardComponent={TradeScreenPropertyCard}
              />
            )
          })}
        </div>
      </div>
    )
  }

  // The strip between the two panels. Both modes state the deal the same way —
  // "You pay M250 to Sara" — composing just makes the amount typeable and adds
  // Swap on the end of the line. With no cash involved at all in an offered
  // trade it degrades to a plain divider.
  const renderCashRow = () => {
    // No cash in an offered trade: nothing to summarise, so it steps back to a
    // divider rather than an empty amber box shouting about zero money.
    if (isViewMode && cashAmount <= 0) {
      return (
        <div className="relative flex items-center py-1">
          <div className="h-px flex-1 bg-white/10" />
          <div className="mx-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/10">
            <i className="fa-solid fa-arrows-up-down text-white/70 text-xs" />
          </div>
          <div className="h-px flex-1 bg-white/10" />
        </div>
      )
    }

    // No amount yet, so the card claims nothing: it drops its amber and the
    // sentence goes grey and conditional ("would pay"). The swap button is the
    // exception: it keeps a solid keycap, in a deep sky rather than amber so it
    // reads as a control of its own and not as part of the cash accent — dark
    // enough that the amber card, once there is an amount, is still the
    // brightest thing in the drawer. Greyed down
    // with the rest of the card it was the one live thing here at M0 wearing
    // the one look that means dead. Lit and in the present tense at M0 the card was
    // announcing a payment over a deal with no money in it at all — the first
    // thing a new player reads and the first thing they get wrong. Turning
    // amber the moment there is an amount also teaches, for free, what amber
    // means everywhere else in the drawer.
    const quiet = !hasCash

    return (
      <div
        // Deliberately the loudest surface in the drawer, and the only one that
        // sits *above* it: amber tint, amber hairline, an outer glow and a solid
        // amber edge down its left side. Both property panels are recessed
        // dark-on-dark by contrast — when all three were the same lifted grey
        // box, the trade's one editable control read as just another panel.
        className={classNames(
          "relative flex flex-col rounded-xl border pl-3.5 pr-2.5",
          quiet
            ? "border-white/10 bg-white/[0.04] shadow-[0_0_0_1px_rgba(0,0,0,0.35)]"
            : "border-amber-300/40 bg-amber-400/10 shadow-[0_0_0_1px_rgba(0,0,0,0.35),0_0_18px_rgba(251,191,36,0.18)]"
        )}
        style={{
          gap: "var(--tc-cash-gap, 8px)",
          paddingBlock: "var(--tc-cash-py, 10px)",
        }}
      >
        {/* Solid accent edge. Costs no vertical space, which matters because
            this card competes with the property cards for the drawer's height. */}
        <span
          className={classNames(
            "pointer-events-none absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full",
            quiet ? "bg-white/20" : "bg-amber-400"
          )}
        />

        {/* What this block is, and what it costs the payer. The consequence
            rides on the title row rather than getting a line of its own: it is
            the answer to "can they even afford this", which is worth a lot to a
            player weighing an amount and nothing at all to the drawer's
            height. */}
        <div
          style={{ display: "var(--tc-caption, flex)" }}
          className="items-center gap-2"
        >
          <i
            className={classNames(
              "fa-solid fa-coins text-xs",
              quiet ? "text-white/40" : "text-amber-300"
            )}
          />
          <span
            className={classNames(
              "font-poppins font-bold text-[0.6875rem] uppercase tracking-[0.09em]",
              quiet ? "text-white/45" : "text-amber-200"
            )}
          >
            Cash
          </span>
          {hasCash ? (
            <span
              data-testid="trade.cash-after"
              title={`${nameOf(cashPayer)} ${
                isYou(cashPayer) ? "have" : "has"
              } M${cashPayer.money} — paying M${cashAmount} leaves M${payerLeft}`}
              className="ml-auto flex min-w-0 items-center gap-1 font-poppins text-[0.6875rem] text-white/55"
            >
              <i className="fa-solid fa-wallet shrink-0 text-[0.625rem] text-white/35" />
              <span className="truncate">
                {nameOf(cashPayer)} {isYou(cashPayer) ? "keep" : "keeps"}{" "}
                <span
                  className={classNames(
                    "font-bold",
                    payerLeft <= 0
                      ? "text-red-300"
                      : payerLeft < 100
                      ? "text-amber-300"
                      : "text-emerald-300"
                  )}
                >
                  M{payerLeft}
                </span>
              </span>
            </span>
          ) : (
            <span className="ml-auto truncate font-poppins text-[0.6875rem] text-white/40">
              No cash in this deal yet
            </span>
          )}
        </div>

        {/* The whole deal as one sentence: "You pay M250 to Sara". Who, which
            way and how much used to be three separate things to decode — two
            boxes captioned PAYS and RECEIVES with the amount somewhere else
            again — and a player had to assemble the sentence themselves before
            they knew what the card was proposing. Now it is written out, with
            the amount as the field inside it, so reading it and setting it are
            the same act. It wraps rather than squashes: on a narrow sidebar the
            line breaks and the sentence stays a sentence. */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 font-poppins text-sm text-white/60">
          <span
            data-testid="trade.cash-payer"
            className="flex shrink-0 items-center gap-1.5"
          >
            {piecePip(cashPayer)}
            <span className="font-semibold text-white">
              {nameOf(cashPayer)}
            </span>
          </span>
          <span className="shrink-0">
            {quiet ? "would pay" : paysVerb(cashPayer)}
          </span>
          {isViewMode ? (
            <span data-testid="trade.cash-amount">
              <Money
                money={cashAmount}
                className="text-amber-300 font-poppins font-bold gap-0.5 text-xl leading-none"
                iconClass="h-1/2"
              />
            </span>
          ) : (
            <span
              className={classNames(
                "inline-flex shrink-0 items-center gap-0.5 rounded-lg border px-2 py-0.5 transition-colors",
                quiet
                  ? "border-white/10 bg-black/25"
                  : "border-amber-300/40 bg-black/30 focus-within:border-amber-300"
              )}
            >
              <span
                className={classNames(
                  "font-fira-sans text-xl leading-none",
                  quiet ? "text-white/40" : "text-amber-300"
                )}
              >
                M
              </span>
              <input
                type="text"
                inputMode="numeric"
                // A payer with nothing can only ever be typed back to 0, and a
                // field that silently swallows what you type is the same
                // species of confusion this card is being cleaned up for. The
                // note underneath says why it's dead and what to do instead.
                disabled={cashPayer.money <= 0}
                value={cashDraft ?? String(cashAmount)}
                onChange={(e) => onCashInput(e.target.value)}
                onFocus={(e) => e.target.select()}
                onBlur={() => setCashDraft(null)}
                aria-label="Cash amount"
                data-testid="trade.cash-amount"
                className={classNames(
                  "w-16 bg-transparent text-center font-poppins font-bold text-xl leading-none outline-none",
                  quiet ? "text-white/40" : "text-white"
                )}
              />
            </span>
          )}
          <span className="shrink-0">to</span>
          <span
            data-testid="trade.cash-receiver"
            className="flex min-w-0 items-center gap-1.5"
          >
            {piecePip(cashReceiver)}
            <span className="min-w-0 truncate font-semibold text-white">
              {objectNameOf(cashReceiver)}
            </span>
          </span>
        </div>

        {/* The swap, written as the sentence it produces. A button labelled
            just "Swap" says an operation is available without ever saying what
            it operates on — and the one thing it operates on, who pays who, is
            the single fact in this card a new player most needs to be able to
            change. So the label carries the result: press it and the line above
            becomes this one. Full width and under the sentence rather than
            beside it, because the outcome text is longer than any sidebar has
            room for at the end of a line.

            Built as a keycap — solid fill, a hard bottom lip, and a press that
            travels the lip's own height — because spelling the outcome out is
            exactly what made it stop reading as a control: a full-width strip
            of small text under a sentence looks like more sentence. The parts
            that say "press me" are therefore structural, not textual: the raised
            edge, the icon in its own recessed chip, and the chevron pointing out
            of the label. */}
        {!isViewMode && forcedCashFromOffering === null && (
          <button
            type="button"
            onClick={flipCashDirection}
            aria-label={`Swap: ${nameOf(cashReceiver)} ${paysVerb(
              cashReceiver
            )} ${objectNameOf(cashPayer)} instead`}
            data-testid="trade.cash-direction-btn"
            className={classNames(
              "group flex w-full shrink-0 cursor-pointer select-none items-center gap-2",
              "h-9 rounded-lg border pl-1.5 pr-2 font-poppins leading-none",
              // The lip is part of the button's resting look, so the press has
              // to eat it: translate down by exactly what the shadow loses, and
              // the whole face moves without the row's layout budging.
              "transition-[background-color,border-color,box-shadow,transform] duration-100",
              "active:translate-y-[2px]",
              quiet
                ? [
                    "border-sky-600 bg-sky-800 text-sky-50",
                    "shadow-[0_3px_0_#0c2b3f]",
                    "hover:bg-sky-700 hover:border-sky-500",
                    "active:shadow-[0_1px_0_#0c2b3f]",
                  ]
                : [
                    "border-amber-300 bg-amber-400 text-[#1b1205]",
                    "shadow-[0_3px_0_#b45309]",
                    "hover:bg-amber-300 hover:border-amber-200",
                    "active:shadow-[0_1px_0_#b45309]",
                  ]
            )}
          >
            <span
              className={classNames(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
                quiet ? "bg-black/25 text-white" : "bg-black/20 text-[#1b1205]"
              )}
            >
              <i aria-hidden className="fa-solid fa-right-left text-xs" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left">
              <span className="text-[0.625rem] font-extrabold uppercase tracking-[0.09em]">
                Swap
              </span>
              <span
                className={classNames(
                  "w-full min-w-0 truncate text-[0.625rem] font-medium",
                  quiet ? "text-sky-100/70" : "text-[#1b1205]/70"
                )}
              >
                {nameOf(cashReceiver)} {paysVerb(cashReceiver)}{" "}
                {objectNameOf(cashPayer)} instead
              </span>
            </span>
            <i
              aria-hidden
              className={classNames(
                "fa-solid fa-chevron-right shrink-0 text-[0.625rem] transition-transform",
                "group-hover:translate-x-0.5",
                quiet ? "text-sky-100/50" : "text-[#1b1205]/50"
              )}
            />
          </button>
        )}

        {/* Ways to set it. Only while composing — an offered trade's amount is
            already stated above and isn't going to move. */}
        {!isViewMode &&
          (cashPayer.money > 0 ? (
            // The slider gets a dark well of its own. On the amber field the
            // yellow track was tinted-on-tinted; cut back to near-black it is
            // the highest-contrast thing in the drawer, which is what a control
            // you have to aim a thumb at should be.
            <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-1">
              <CompactMoneySlider
                max={cashPayer.money}
                min={0}
                value={cashAmount}
                setValue={applyCash}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-2.5 py-2">
              <i className="fa-solid fa-circle-info text-amber-300/80 text-xs shrink-0" />
              <span className="min-w-0 text-sm font-poppins text-amber-100/80 leading-snug">
                {cashReceiver.money > 0 ? (
                  <>
                    {nameOf(cashPayer)} {isYou(cashPayer) ? "have" : "has"} no
                    cash — use Swap to send it the other way.
                  </>
                ) : (
                  // Neither side can pay, so there is nothing for Swap to swap
                  // to — pointing at it would just bounce the player between two
                  // dead fields.
                  <>Neither player has cash to put into this deal.</>
                )}
              </span>
            </div>
          ))}
      </div>
    )
  }

  // Neither side can put cash in, so the row has nothing to open a sheet for.
  const noCashPossible = cashPayer.money <= 0 && cashReceiver.money <= 0

  // The phone's cash row: the same sentence the card leads with, and nothing
  // else. The card's other three parts — the caption, the slider well and the
  // Swap keycap — are what a ~200px drawer has no room for, so they move into
  // MobileCashSheet and this row becomes the way in. Worth about 60px, which
  // goes back to the property cards: the drawer is height-capped and the cards
  // are what shrinks to fit it (useFitTradeCards), so a shorter cash block is
  // bigger property thumbnails, not empty space.
  const renderMobileCashRow = () => {
    const quiet = !hasCash
    return (
      <button
        type="button"
        disabled={noCashPossible}
        onClick={() => setCashSheetOpen(true)}
        data-testid="trade.cash-row-btn"
        aria-label="Edit the cash in this deal"
        className={classNames(
          "relative flex w-full shrink-0 items-center gap-[5px] rounded-lg border py-[2px] pl-[5px] pr-[4px] text-left",
          "transition-[transform,box-shadow] duration-100",
          noCashPossible
            ? "border-white/10 bg-white/[0.04]"
            : [
                // Built as a keycap, like the drawer's Swap button: a hard
                // bottom lip and a press that travels exactly the lip's height,
                // so the row visibly gives without the stack above and below it
                // moving. On a phone this row is the *only* way to the amount —
                // the caption, the slider and Swap all live in the sheet behind
                // it — so it has to read as a door rather than as a summary
                // line, which a flat tinted strip with a chevron did not.
                "active:translate-y-[2px]",
                quiet
                  ? "border-white/20 bg-white/[0.07] shadow-[0_2px_0_rgba(0,0,0,0.6)] active:shadow-none"
                  : // The lip, plus the same amber glow the full card carries —
                    // this row stands in for that card on a phone, so it keeps
                    // its place as the loudest surface in the drawer.
                    "border-amber-300/50 bg-amber-400/10 shadow-[0_2px_0_#b45309,0_0_18px_rgba(251,191,36,0.18)] active:shadow-[0_0_18px_rgba(251,191,36,0.18)]",
              ]
        )}
      >
        <i
          className={classNames(
            "fa-solid fa-coins shrink-0 text-[11px]",
            quiet ? "text-white/40" : "text-amber-300"
          )}
        />
        {noCashPossible ? (
          <span className="min-w-0 flex-1 truncate font-poppins text-[11px] text-white/40">
            Neither player has cash
          </span>
        ) : (
          // The sentence broken over two lines instead of squashed into one.
          // In a ~200px drawer the single line had to carry two names, a verb,
          // an amount and an arrow, and the only thing that could give was the
          // receiver's name — so the half of the sentence that says where the
          // money goes was the half that got cut. Two lines let every part
          // shrink instead: smaller type throughout, and room for both players'
          // pieces, which are what tie each side of the row back to the banner
          // it came from.
          <span
            data-testid="trade.cash-amount"
            className="flex min-w-0 flex-1 flex-col gap-[2px] font-poppins text-[10px] leading-none text-white/55"
          >
            <span className="flex min-w-0 items-center gap-[4px]">
              {piecePipPx(cashPayer)}
              <span className="min-w-0 truncate font-semibold text-white">
                {nameOf(cashPayer)}
              </span>
              <span className="shrink-0">
                {quiet ? "would pay" : paysVerb(cashPayer)}
              </span>
            </span>
            {/* The amount leads the second line: it is the one thing on this
                row that the sheet behind it exists to change. */}
            <span className="flex min-w-0 items-center gap-[4px]">
              <span
                className={classNames(
                  "shrink-0 font-fira-sans font-bold text-[12px] leading-none",
                  quiet ? "text-white/40" : "text-amber-300"
                )}
              >
                M{cashAmount}
              </span>
              <span className="shrink-0">to</span>
              {piecePipPx(cashReceiver)}
              <span className="min-w-0 truncate font-semibold text-white">
                {objectNameOf(cashReceiver)}
              </span>
            </span>
          </span>
        )}
        {/* A pencil rather than a chevron. The chevron said "there is more this
            way"; the point is that the amount is edited here, and nowhere else
            on a phone. */}
        {!noCashPossible && (
          <span
            className={classNames(
              "flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-md",
              quiet ? "bg-white/10 text-white/70" : "bg-amber-400/25 text-amber-100"
            )}
          >
            <i aria-hidden className="fa-solid fa-pen text-[9px]" />
          </span>
        )}
      </button>
    )
  }

  // Minimized: the drawer folds down to a pill in the same corner. On a phone
  // "doesn't disturb the game" can't mean "is small" — the drawer is already a
  // quarter of the screen and can't usefully be smaller — so it means "is one
  // tap out of the way". The draft is untouched and the board stays in trade
  // mode, so properties can still be picked off it while the pill is down.
  if (isMobile && minimized) {
    const givenCount = activeTrade.givenProperties?.length || 0
    const takenCount = activeTrade.takenProperties?.length || 0
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        data-testid="trade.restore-btn"
        aria-label="Reopen the trade drawer"
        className={classNames(
          "fixed left-[var(--safe-left)] bottom-[var(--safe-bottom)] z-[120]",
          "flex h-[38px] items-center gap-[8px] rounded-2xl border border-amber-300/30 pl-[10px] pr-[8px]",
          "bg-[#14121b] shadow-[0_4px_16px_rgba(0,0,0,0.6)] active:scale-95 transition-transform"
        )}
      >
        <i className="fa-solid fa-right-left shrink-0 text-[13px] text-amber-300" />
        <span className="flex items-center gap-[6px] font-poppins text-[12px] leading-none text-white/85">
          {/* Same two arrows the player banners use, so the pill reads as the
              drawer folded up rather than as a new piece of chrome. */}
          <span className="flex items-center gap-[2px] font-semibold">
            {givenCount}
            <i className="fa-solid fa-arrow-down text-[9px] text-white/50" />
          </span>
          <span className="flex items-center gap-[2px] font-semibold">
            {takenCount}
            <i className="fa-solid fa-arrow-up text-[9px] text-white/50" />
          </span>
          {hasCash && (
            <span className="font-fira-sans font-bold text-amber-300">
              M{cashAmount}
            </span>
          )}
        </span>
        <i className="fa-solid fa-chevron-up shrink-0 text-[10px] text-white/40" />
      </button>
    )
  }

  return (
    <div
      ref={panelRef}
      data-testid="trade"
      className={classNames(
        // Bottom-left docked drawer. Rendered at the Game page top level so
        // `fixed` anchors to the viewport (not to the board's filtered #center).
        // Anchored to the bottom so it grows upward over the bank slot and, when
        // the deal is big enough to need the room, over the player cards too —
        // composing a trade is a modal task, and both traders' pieces, names and
        // balances are on the drawer's own banners while it is up.
        //
        // It takes the *whole* width of the left sidebar (SidebarLeft is
        // (100vw-100vh)/2 wide via the page grid) and most of its height. It is
        // still content-sized: the cap is only the ceiling the property cards
        // are shrunk against (useFitTradeCards), never a height it pads out to.
        //
        // Safe area: `fixed` anchors to the viewport, so this ignores the inset
        // padding the Game page puts on itself, and on a phone (where App.css
        // floors the gutter at 8px) its content was running under the notch and
        // the home indicator. It is taken as *padding* rather than as an offset:
        // pulled off the corner the panel stops being a drawer and becomes a
        // floating card, so the surface stays welded to the screen's edge and
        // runs on under the cutouts, with only the content held inside the safe
        // box. Width and height cap grow by the same insets, so that content
        // column keeps the exact size — and the exact edges of the sidebar —
        // that it has on a screen with no cutouts at all; the gutter is filled
        // by the drawer's own dark surface, which is what keeps it attached.
        "fixed left-0 bottom-0 z-[120] flex flex-col",
        "pl-[var(--safe-left)] pb-[var(--safe-bottom)]",
        "w-[calc(var(--safe-left)_+_(var(--play-w)_-_100vh)/2)]",
        "max-h-[calc(var(--play-h)*0.82_+_var(--safe-bottom))]",
        // The drawer is content-height, so a deal between two players holding
        // two properties each is a short box — and the cash sheet covering it
        // is `absolute inset-0`, which means it inherits exactly that height.
        // While the sheet is up the drawer is pinned to its own cap instead:
        // the same number the max-height uses, so the two can never fight, and
        // the sheet always opens into the largest box the drawer is allowed.
        cashSheetOpen &&
          "min-h-[calc(var(--play-h)*0.82_+_var(--safe-bottom))]",
        "rounded-tr-2xl border-r border-t border-white/10 overflow-hidden",
        // One flat, fully opaque surface. No gradient and no backdrop blur, so
        // the busy board behind it never bleeds through and text stays legible.
        "bg-[#14121b]",
        "shadow-[8px_0_24px_rgba(0,0,0,0.6)]",
        "transition-transform duration-300 ease-out",
        open ? "translate-x-0" : "-translate-x-full"
      )}
    >
      {/* Header */}
      <div className="relative flex items-center gap-2.5 shrink-0 px-3.5 py-2.5 border-b border-white/10">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-400/15 ring-1 ring-amber-300/25">
          <i className="fa-solid fa-right-left text-amber-300 text-base" />
        </span>
        <h2 className="min-w-0 flex-1 font-poppins font-semibold text-white text-lg leading-tight flex items-center gap-2">
          <span className="truncate">{statusText()}</span>
          {originBadge && (
            <span
              data-testid="trade.origin-badge"
              className={classNames(
                "shrink-0 flex items-center gap-1 rounded-full border px-2 py-1",
                "font-poppins font-semibold text-[0.6875rem] uppercase tracking-wide",
                originBadge.className
              )}
            >
              <i className={classNames(originBadge.icon, "text-[0.625rem]")} />
              {originBadge.label}
            </span>
          )}
          {isViewMode && isOfferingPlayer && (
            // height must be given explicitly: it defaults to 80, which would
            // inflate the header row (the dots draw the same either way, since
            // the 120x30 viewBox is scaled by the width).
            <ThreeDots
              width={24}
              height={6}
              color="rgb(252 211 77)"
              wrapperClass="shrink-0"
            />
          )}
        </h2>
        {/* Fold the drawer away without dropping the draft. Only on a phone:
            on a desktop the drawer sits in spare sidebar width and never covers
            anything worth uncovering. */}
        {isMobile && (
          <button
            type="button"
            onClick={() => {
              setCashSheetOpen(false)
              setMinimized(true)
            }}
            data-testid="trade.minimize-btn"
            aria-label="Minimize the trade drawer"
            className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-full bg-white/10 text-white/70 active:scale-90"
          >
            <i className="fa-solid fa-chevron-down text-[13px]" />
          </button>
        )}
        <ModalCloseButton
          onClick={requestClose}
          data-testid="trade.close-btn"
          // The px override that keeps this button from dwarfing the header has
          // to be re-stated in px on a phone: `!w-8` is rem, and the root font
          // size is halved below 1000px, so it was landing at a 16px target.
          className={classNames(
            "shrink-0",
            isMobile ? "!w-[32px] !h-[32px]" : "!w-8 !h-8"
          )}
        />
      </div>

      {/* Helper note — tells the player composing the offer that they can pick
          properties from either the board or the cards below. It stays put for
          the whole compose session: picking one property doesn't mean they know
          the board is still tappable for the rest. */}
      {!isViewMode && isOfferingPlayer && (
        <div className="flex items-center gap-2 px-3.5 pt-2 shrink-0">
          <i className="fa-solid fa-hand-pointer text-white/60 text-sm shrink-0" />
          <span className="flex-1 min-w-0 text-white/70 text-sm font-poppins leading-snug">
            {isUpdateMode
              ? // Spelled out because the current offer is still live and still
                // counting down while this edit is open — Discard leaving it
                // standing is otherwise easy to read as "cancel the trade".
                "Edit the offer, then Offer to replace it. Discard keeps the current one."
              : isNegotiateMode
              ? // Same reasoning, plus the part that is unique to negotiating:
                // the sides have just swapped, so it must be clear the counter
                // goes back to them and that backing out is free.
                "Change the deal, then Offer to send it back. Discard leaves their offer as it is."
              : "Tap properties on the board or the cards below to include them."}
          </span>
          {/* Reset lives on this row rather than in the header: next to the
              "what you can pick" line it reads as the undo for picking, and it
              gets the contrast (amber, like the other actionable accents) that
              a grey chip squeezed between the title and the close button never
              had. */}
          {showReset && (
            <button
              onClick={resetSelections}
              disabled={!hasAnySelection}
              title="Clear everything selected"
              data-testid="trade.reset-btn"
              className={classNames(
                "shrink-0 flex items-center gap-1.5 rounded-md border px-2.5 h-7",
                "font-poppins font-semibold text-xs uppercase tracking-wide transition-colors",
                hasAnySelection
                  ? "border-amber-300/40 bg-amber-400/20 text-amber-200 hover:bg-amber-400/30 hover:border-amber-300/60 active:scale-[0.98]"
                  : "border-white/10 bg-white/5 text-white/30 cursor-default"
              )}
            >
              <i className="fa-solid fa-rotate-left text-[0.6875rem]" />
              Reset
            </button>
          )}
        </div>
      )}

      {/* Panels — offering player on top, offered on the bottom, with the trade's
          single cash row between them. Everything is content-height: the drawer
          is only as tall as it needs to be, and when that would exceed the cap
          the property cards shrink rather than anything scrolling or clipping. */}
      <div
        className="flex flex-col shrink-0 px-3.5"
        style={{
          gap: "var(--tc-stack-gap, 8px)",
          paddingBlock: "var(--tc-pad-y, 10px)",
        }}
      >
        {renderPanel(offeringPlayer, offeringProperties, "down")}
        {/* Composing on a phone is the only case that collapses: an offered
            trade's cash card has no controls on it to begin with — just the
            sentence — so it is already the height this row would be. */}
        {isMobile && !isViewMode ? renderMobileCashRow() : renderCashRow()}
        {renderPanel(offeredPlayer, offeredProperties, "up")}
      </div>

      {/* Action buttons — omitted entirely when the viewer has no action to
          take (e.g. a player inspecting someone else's trade). */}
      {(showOffer ||
        showAcceptDecline ||
        showWithdraw ||
        showUpdateActions ||
        showNegotiateActions) && (
        // The buttons stretch to fill the row rather than sitting centred at
        // their text width: in a drawer this narrow that is the difference
        // between three cramped pills and three comfortable targets.
        <div className="flex items-center gap-2 px-3.5 pb-3 pt-1 shrink-0">
          {showOffer && (
            <TradeActionButton
              tone="primary"
              icon="fa-solid fa-paper-plane"
              onClick={onClickOnOffer}
              data-testid="trade.offer-btn"
            >
              Offer Trade
            </TradeActionButton>
          )}
          {showAcceptDecline && (
            <>
              <TradeActionButton
                tone="accept"
                icon="fa-solid fa-check"
                onClick={onClickOnAccept}
                data-testid="trade.accept-btn"
              >
                Accept
              </TradeActionButton>
              {/* Sits between the two outright answers, because that is what it
                  is: neither yes nor no, but a counter. */}
              <TradeActionButton
                tone="counter"
                icon="fa-solid fa-reply"
                onClick={onClickOnNegotiate}
                data-testid="trade.negotiate-btn"
              >
                Negotiate
              </TradeActionButton>
              <TradeActionButton
                tone="danger"
                icon="fa-solid fa-xmark"
                onClick={onClickOnDecline}
                data-testid="trade.decline-btn"
              >
                Decline
              </TradeActionButton>
            </>
          )}
          {showUpdate && (
            <TradeActionButton
              tone="primary"
              icon="fa-solid fa-pen-to-square"
              onClick={onClickOnUpdate}
              data-testid="trade.update-btn"
            >
              Update
            </TradeActionButton>
          )}
          {showWithdraw && (
            <TradeActionButton
              tone="danger"
              icon="fa-solid fa-ban"
              onClick={onClickOnWithdraw}
              data-testid="trade.decline-btn"
            >
              Withdraw
            </TradeActionButton>
          )}
          {showUpdateActions && (
            <>
              <TradeActionButton
                tone="primary"
                icon="fa-solid fa-paper-plane"
                onClick={onClickOnUpdatedOffer}
                data-testid="trade.offer-btn"
              >
                Offer
              </TradeActionButton>
              {/* Leaves the offer that is already on the table untouched — this
                  only throws away the edits. */}
              <TradeActionButton
                tone="ghost"
                icon="fa-solid fa-xmark"
                onClick={onClickOnDiscard}
                data-testid="trade.discard-btn"
              >
                Discard
              </TradeActionButton>
            </>
          )}
          {showNegotiateActions && (
            <>
              <TradeActionButton
                tone="primary"
                icon="fa-solid fa-paper-plane"
                onClick={onClickOnNegotiatedOffer}
                data-testid="trade.offer-btn"
              >
                Offer
              </TradeActionButton>
              {/* Walks away from the counter only: the offer being negotiated
                  is untouched and still waiting for an answer. */}
              <TradeActionButton
                tone="ghost"
                icon="fa-solid fa-xmark"
                onClick={onClickOnDiscard}
                data-testid="trade.discard-btn"
              >
                Discard
              </TradeActionButton>
            </>
          )}
        </div>
      )}

      {/* Bottom breathing room while viewing a trade that has no action row
          (e.g. a spectator), so the content doesn't hug the drawer's edge. */}
      {isViewMode && !(showAcceptDecline || showWithdraw) && (
        <div className="h-3 shrink-0" />
      )}

      {/* The cash editor, covering the drawer's own box. Inside the panel and
          `absolute inset-0`, so it is bounded by exactly the height the fitting
          hook has already settled on — measure it as a sibling row instead and
          the property cards would shrink to make room for a sheet that is
          sitting on top of them. */}
      {cashSheetOpen && isMobile && !isViewMode && (
        <MobileCashSheet
          amount={cashAmount}
          max={cashPayer.money}
          payerName={nameOf(cashPayer)}
          payerKeepsVerb={isYou(cashPayer) ? "keep" : "keeps"}
          paysVerb={paysVerb(cashPayer)}
          payerLeft={payerLeft}
          receiverName={objectNameOf(cashReceiver)}
          receiverHasCash={cashReceiver.money > 0}
          draft={cashDraft}
          onInput={onCashInput}
          onInputBlur={() => setCashDraft(null)}
          setAmount={applyCash}
          onSwap={
            forcedCashFromOffering === null ? flipCashDirection : undefined
          }
          swapLabel={`${nameOf(cashReceiver)} ${paysVerb(
            cashReceiver
          )} ${objectNameOf(cashPayer)} instead`}
          onDone={() => setCashSheetOpen(false)}
        />
      )}
    </div>
  )
}

export default CreateTradeModal
